import { useEffect, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../../lib/supabase'
import { useInactivitySignOut } from '../../lib/useInactivitySignOut'

type Status = 'checking' | 'authorized' | 'unauthorized'

/**
 * Keeps a workspace route reachable only by a signed-in account whose
 * `profiles` role matches it, so typing /agent or /supervisor without a
 * session, or with the other role, lands on the sign-in page instead of the
 * workspace. This is a client-side redirect, not the access boundary: the
 * real boundary is row-level security once these screens read real data,
 * the same way docs/design/style-guide.md's stack treats it everywhere
 * else. This just keeps the URL from showing a signed-out visitor a
 * workspace that is not theirs.
 */
export default function RequireRole({ role, children }: { role: string | string[]; children: ReactNode }) {
  // A page shared by several roles lists them; a workspace names its one.
  const roles = Array.isArray(role) ? role : [role]
  const rolesKey = roles.join(',')
  const [status, setStatus] = useState<Status>('checking')

  // A signed-in workspace ends its session after a period without
  // interaction; the sign-out reaches the subscription below like any other.
  useInactivitySignOut(status === 'authorized')

  useEffect(() => {
    let cancelled = false
    // Each auth event's profile query resolves independently, and an older
    // one can still be in flight when a newer event fires. Only the result
    // whose event is still the latest gets applied, so a stale query cannot
    // overwrite what a more recent one already decided.
    let latestEvent = 0

    async function check(session: Session | null, eventId: number) {
      const applies = () => !cancelled && eventId === latestEvent

      if (!session) {
        if (applies()) setStatus('unauthorized')
        return
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single()

      if (applies()) setStatus(profile?.role && rolesKey.split(',').includes(profile.role) ? 'authorized' : 'unauthorized')
    }

    // Subscribed rather than checked once: a session that ends while this
    // workspace is already open (a sign-out in another tab, a refresh that
    // fails) has to pull the workspace down too, and fires here as soon as
    // Supabase notices. Fires once immediately with the current session, so
    // this also covers the first check on mount.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      latestEvent += 1
      void check(session, latestEvent)
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [rolesKey])

  if (status === 'checking') {
    return (
      <div className="flex h-dvh items-center justify-center bg-canvas">
        <p role="status" className="text-small text-ink-muted">
          Checking your session&hellip;
        </p>
      </div>
    )
  }

  if (status === 'unauthorized') return <Navigate to="/" replace />

  return <>{children}</>
}
